import { store } from '../core/store.js';
import { refs } from '../core/refs.js';
import { isJsPending, runCode, RUN_TRIGGERS } from './runner.js';
import { escapeHtml } from '../core/escape.js';
import { tokenizeCodeParsedAsync } from '../core/wasm.js';
import { recordEditorValueUpdate } from './runner/timing.js';
import { createLiveScheduler } from './live-scheduler.js';
import { captureAnalyticsEvent } from '../core/analytics.js';

const LARGE_HIGHLIGHT_BYTES = 80 * 1024;
const VERY_LARGE_HIGHLIGHT_BYTES = 240 * 1024;
const VISIBLE_LINE_BUFFER = 80;
const MAX_WINDOWED_HIGHLIGHT_BYTES = 48 * 1024;
const VERY_LARGE_IDLE_HIGHLIGHT_MS = 900;
let jsPendingHintShown = false;
const highlightJobs = new WeakMap();
const highlightVersions = new WeakMap();
const idleHighlightJobs = new WeakMap();
const highlightSnapshots = new WeakMap();
let activeEditor = null;
let liveServices = {
  console: null
};

export function configureLiveReflection(services = {}) {
  liveServices = {
    ...liveServices,
    ...services
  };
}

function getOverlayFor(editor) {
  if (editor === refs.htmlEditor) return refs.htmlOverlay;
  if (editor === refs.cssEditor) return refs.cssOverlay;
  if (editor === refs.jsEditor) return refs.jsOverlay;
  return null;
}

function getLangFor(editor) {
  if (editor === refs.htmlEditor) return 'html';
  if (editor === refs.cssEditor) return 'css';
  if (editor === refs.jsEditor) return 'js';
  return '';
}

function editorEntries() {
  return [
    { editor: refs.htmlEditor, overlay: refs.htmlOverlay, lang: 'html' },
    { editor: refs.cssEditor, overlay: refs.cssOverlay, lang: 'css' },
    { editor: refs.jsEditor, overlay: refs.jsOverlay, lang: 'js' }
  ].filter(({ editor, overlay }) => editor && overlay);
}

function getEditorForActiveTab() {
  return {
    html: refs.htmlEditor,
    css: refs.cssEditor,
    js: refs.jsEditor
  }[store.get('activeTab')];
}

function getActiveEditor() {
  const focused = document.activeElement;
  if (focused === refs.htmlEditor || focused === refs.cssEditor || focused === refs.jsEditor) {
    return focused;
  }
  return activeEditor || getEditorForActiveTab() || refs.htmlEditor;
}

function getHighlightMode(code) {
  const size = code.length;
  if (size >= VERY_LARGE_HIGHLIGHT_BYTES) return 'idle';
  if (size >= LARGE_HIGHLIGHT_BYTES) return 'windowed';
  return 'full';
}

function getLineHeight(editor) {
  const parsed = Number.parseFloat(window.getComputedStyle(editor).lineHeight);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 21;
}

function computeLineWindow(code, editor) {
  const lineHeight = getLineHeight(editor);
  const firstVisibleLine = Math.max(0, Math.floor(editor.scrollTop / lineHeight));
  const visibleLines = Math.max(1, Math.ceil(editor.clientHeight / lineHeight));
  const startLine = Math.max(0, firstVisibleLine - VISIBLE_LINE_BUFFER);
  const endLine = startLine + visibleLines + VISIBLE_LINE_BUFFER * 2;
  let currentLine = 0;
  let startOffset = startLine === 0 ? 0 : code.length;
  let endOffset = code.length;

  for (let index = 0; index < code.length; index += 1) {
    if (code.charCodeAt(index) !== 10) continue;
    currentLine += 1;
    if (currentLine === startLine) {
      startOffset = index + 1;
    }
    if (currentLine === endLine && endOffset === code.length) {
      endOffset = index + 1;
    }
  }

  const totalLines = currentLine + 1;
  const normalizedStartLine = Math.min(startLine, Math.max(0, totalLines - 1));
  const normalizedEndLine = Math.min(endLine, totalLines);
  return {
    startLine: normalizedStartLine,
    endLine: normalizedEndLine,
    totalLines,
    startOffset,
    endOffset,
    code: code.slice(startOffset, endOffset)
  };
}

function setHighlightDisabled(editor, overlay, disabled) {
  const wrapper = editor?.closest('.editor-wrapper');
  if (wrapper) {
    wrapper.classList.toggle('syntax-highlight-disabled', disabled);
  }
  if (disabled && overlay) {
    overlay.textContent = '';
  }
}

function renderHighlightFragment(code, tokens) {
  let html = '';
  let lastIdx = 0;
  for (const t of tokens) {
    if (t.start >= code.length) continue;
    const end = Math.min(code.length, t.start + t.len);
    if (t.start > lastIdx) {
      html += escapeHtml(code.slice(lastIdx, t.start));
    }
    html += `<span class="token-${t.type}">${escapeHtml(code.slice(t.start, end))}</span>`;
    lastIdx = end;
  }
  if (lastIdx < code.length) {
    html += escapeHtml(code.slice(lastIdx));
  }
  if (code.endsWith('\n')) {
    html += '\n';
  }
  return html;
}

/**
 * Parses and updates the syntax highlight spans inside the overlay in-sync with textarea.
 */
function renderHighlights(code, tokens, overlay) {
  overlay.innerHTML = renderHighlightFragment(code, tokens);
}

function renderWindowedHighlights(windowState, tokens, overlay) {
  const prefix = '\n'.repeat(windowState.startLine);
  const suffix = '\n'.repeat(Math.max(0, windowState.totalLines - windowState.endLine));
  overlay.innerHTML = prefix + renderHighlightFragment(windowState.code, tokens) + suffix;
}

function isHighlightCurrent(editor, expectedVersion) {
  return expectedVersion === null || highlightVersions.get(editor) === expectedVersion;
}

function cancelIdleHighlight(editor) {
  const pending = idleHighlightJobs.get(editor);
  if (!pending) return;
  if (pending.type === 'timeout') {
    clearTimeout(pending.id);
  } else if (typeof cancelIdleCallback === 'function') {
    cancelIdleCallback(pending.id);
  }
  idleHighlightJobs.delete(editor);
}

function scheduleIdleHighlight(editor, overlay, lang, expectedVersion) {
  cancelIdleHighlight(editor);
  const run = () => {
    idleHighlightJobs.delete(editor);
    void updateHighlights(editor, overlay, lang, expectedVersion, { allowVeryLarge: true });
  };
  const id = typeof requestIdleCallback === 'function'
    ? requestIdleCallback(run, { timeout: VERY_LARGE_IDLE_HIGHLIGHT_MS })
    : setTimeout(run, VERY_LARGE_IDLE_HIGHLIGHT_MS);
  idleHighlightJobs.set(editor, {
    id,
    type: typeof requestIdleCallback === 'function' ? 'idle' : 'timeout'
  });
}

export async function updateHighlights(editor, overlay, lang, expectedVersion = null, options = {}) {
  if (!editor || !overlay) return;
  const code = editor.value;
  const mode = getHighlightMode(code);
  if (mode === 'idle' && !options.allowVeryLarge) {
    setHighlightDisabled(editor, overlay, true);
    scheduleIdleHighlight(editor, overlay, lang, expectedVersion);
    return;
  }

  if (!isHighlightCurrent(editor, expectedVersion)) return;

  if (mode === 'windowed' || mode === 'idle' || options.forceWindowed) {
    const windowState = computeLineWindow(code, editor);
    if (windowState.code.length > MAX_WINDOWED_HIGHLIGHT_BYTES) {
      setHighlightDisabled(editor, overlay, true);
      return;
    }

    const snapshot = `${windowState.startOffset}:${windowState.endOffset}:${code.length}:${editor.scrollTop}`;
    if (highlightSnapshots.get(editor) === snapshot && !options.force) return;

    const tokens = await tokenizeCodeParsedAsync(windowState.code, lang);
    if (editor.value !== code || !isHighlightCurrent(editor, expectedVersion)) return;
    setHighlightDisabled(editor, overlay, false);
    renderWindowedHighlights(windowState, tokens, overlay);
    highlightSnapshots.set(editor, snapshot);
    return;
  }

  const tokens = await tokenizeCodeParsedAsync(code, lang);
  if (editor.value !== code || !isHighlightCurrent(editor, expectedVersion)) return;
  setHighlightDisabled(editor, overlay, false);
  renderHighlights(code, tokens, overlay);
  highlightSnapshots.set(editor, `full:${code.length}`);
}

function scheduleHighlight(editor, overlay, lang, options = {}) {
  if (!editor || !overlay) return;
  const pending = highlightJobs.get(editor);
  if (pending) cancelAnimationFrame(pending);
  cancelIdleHighlight(editor);

  const version = (highlightVersions.get(editor) || 0) + 1;
  highlightVersions.set(editor, version);
  const next = requestAnimationFrame(() => {
    highlightJobs.delete(editor);
    void updateHighlights(editor, overlay, lang, version, options);
  });
  highlightJobs.set(editor, next);
}

/**
 * Triggers re-highlighting on all active editor panels.
 */
export function triggerAllHighlights() {
  const entries = editorEntries();
  const largeWorkspace = entries.some(({ editor }) => getHighlightMode(editor.value) !== 'full');
  const preferredEditor = getActiveEditor();
  entries.forEach(({ editor, overlay, lang }) => {
    if (largeWorkspace && editor !== preferredEditor && getHighlightMode(editor.value) !== 'full') {
      setHighlightDisabled(editor, overlay, true);
      return;
    }
    scheduleHighlight(editor, overlay, lang, { force: true });
  });
}

/**
 * Front-my-End — Live preview & syntax highlighting
 *
 * Two independent features share each editor's input listener:
 * - Syntax highlighting (always on) via the Rust WASM lexer.
 * - Live preview (toggle): CSS hot-swap after a short pause, full HTML
 *   re-render after a longer pause, never editor JavaScript.
 */
export function registerLiveReflection() {
  const editors = [refs.htmlEditor, refs.cssEditor, refs.jsEditor];
  editors.forEach((editor) => {
    if (!editor) return;
    const overlay = getOverlayFor(editor);
    const lang = getLangFor(editor);

    // Sync scroll offsets
    editor.addEventListener('scroll', () => {
      if (overlay) {
        overlay.scrollTop = editor.scrollTop;
        overlay.scrollLeft = editor.scrollLeft;
        if (getHighlightMode(editor.value) !== 'full') {
          scheduleHighlight(editor, overlay, lang);
        }
      }
    });

    editor.addEventListener('focus', () => {
      activeEditor = editor;
      if (getHighlightMode(editor.value) !== 'full') {
        scheduleHighlight(editor, overlay, lang, { force: true });
      }
    });

    editor.addEventListener('input', () => {
      activeEditor = editor;
      // Sync editor textarea contents to the reactive store
      recordEditorValueUpdate(lang, () => {
        if (editor === refs.htmlEditor) store.set('html', editor.value);
        if (editor === refs.cssEditor) store.set('css', editor.value);
        if (editor === refs.jsEditor) store.set('js', editor.value);
      });

      scheduleHighlight(editor, overlay, lang);

      scheduleLive(lang);
    });
  });

  store.subscribe('liveEnabled', (val) => {
    applyLiveUI(val);
  });
}

function runLive() {
  return runCode({ trigger: RUN_TRIGGERS.IMPLICIT, silent: true, allowHotSwap: true });
}

function syncJsPendingState() {
  const pending = isJsPending();
  const runBtn = refs.runBtn;
  if (runBtn) {
    if (pending) runBtn.setAttribute('data-js-pending', 'true');
    else runBtn.removeAttribute('data-js-pending');
  }
  if (!pending) {
    jsPendingHintShown = false;
    return;
  }
  if (store.get('liveEnabled') && !jsPendingHintShown) {
    jsPendingHintShown = true;
    liveServices.console?.system('JavaScript changes run when you press Run. Live applies CSS instantly and HTML after a pause.');
  }
}

const liveScheduler = createLiveScheduler({
  run: () => { void runLive(); },
  isEnabled: () => Boolean(store.get('liveEnabled')),
  onJsEdit: syncJsPendingState
});

function scheduleLive(lang) {
  liveScheduler.schedule(lang);
}

/** Re-evaluates the Run button's pending-JavaScript marker (after Run, load, import). */
export function refreshJsPendingState() {
  syncJsPendingState();
}

export function setLive(enabled) {
  store.set('liveEnabled', enabled);
  captureAnalyticsEvent('live_toggled', { enabled: Boolean(enabled) });
  if (!enabled) {
    liveScheduler.cancel();
  }
  liveServices.console?.system(enabled
    ? 'Live preview enabled: CSS applies instantly and HTML after a pause. JavaScript runs when you press Run.'
    : 'Live preview disabled.');
  if (enabled) {
    void runLive();
  }
}

function applyLiveUI(enabled) {
  if (refs.liveBtn) {
    refs.liveBtn.classList.toggle('active', enabled);
    refs.liveBtn.setAttribute('aria-pressed', String(enabled));
  }
}
